import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { RotateCcw, X, PaintBucket, Maximize, Layers } from "lucide-react";

const ShapeToolbar = ({
  questionId,
  isLeftSidebarOpen,
  leftSidebarWidth = 0,
  isRightSidebarOpen,
  windowWidth,
  selectedComponent,
  onComponentUpdate,
  onDelete,
  onDeselect,
}) => {
  const [isMobile, setIsMobile] = useState(windowWidth < 768);

  useEffect(() => {
    setIsMobile(windowWidth < 768);
  }, [windowWidth]);

  const calculatePosition = () => {
    if (windowWidth < 768) {
      return { left: "50%", transform: "translateX(-50%)" };
    }
    const leftSidebarOffset = isLeftSidebarOpen ? leftSidebarWidth / 2 : 0;
    const rightSidebarOffset = isRightSidebarOpen ? -32 : 0; // right sidebar width (64) / 2
    const totalOffset = leftSidebarOffset + rightSidebarOffset;
    const leftOffset =
      totalOffset !== 0 ? `calc(50% + ${totalOffset}px)` : "50%";
    return { left: leftOffset, transform: "translateX(-50%)" };
  };

  const position = calculatePosition();

  const handleChange = (field, value) => {
    if (!selectedComponent) return;
    onComponentUpdate(questionId, selectedComponent.id, { [field]: value });
  };

  const handleRotate = () => {
    if (!selectedComponent) return;
    const current = parseInt(selectedComponent.rotation || 0, 10) || 0;
    const next = (current + 45) % 360;
    handleChange("rotation", next);
  };

  // delete action intentionally omitted in toolbar per design refinement

  return (
    <div
      className="fixed z-50 transition-all duration-300"
      style={{
        left: position.left,
        transform: position.transform,
        bottom: isMobile ? "calc(34px + env(safe-area-inset-bottom, 0px))" : 34,
      }}
    >
      <motion.div
        key={questionId}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="bg-white rounded-xl shadow-xl border border-gray-200 relative"
        style={{
          width: isMobile ? "calc(100vw - 24px)" : "600px",
          maxWidth: "calc(100vw - 24px)",
          maxHeight: isMobile ? "40vh" : "none",
          overflowY: isMobile ? "auto" : "visible",
        }}
      >
        <button
          onClick={onDeselect}
          className={`${
            isMobile ? "top-2 right-2" : "-top-3 right-2"
          } absolute bg-white rounded-full ${
            isMobile ? "p-1" : "p-1.5"
          } shadow-md border border-gray-200`}
          aria-label="Close"
          title="Close"
          type="button"
        >
          <X size={isMobile ? 14 : 16} className="text-gray-700" />
        </button>

        {/* Delete removed per design refinement */}

        <div className={`${isMobile ? "px-2 py-3" : "px-3 py-3"} w-full`}>
          <div
            className="grid gap-3"
            style={{
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            }}
          >
            {/* Row 1 - Col 1: Background color */}
            <div className="flex flex-col gap-2">
              <p className="text-xs text-black font-semibold flex items-center gap-1">
                <PaintBucket size={14} className="text-gray-600" /> Fill Color
              </p>
              <label className="flex items-center gap-2">
                <div className="flex justify-center items-center w-10 h-10 overflow-hidden rounded-lg shadow-lg border border-gray-200">
                  <input
                    type="color"
                    value={selectedComponent?.backgroundColor ?? "#ffffff"}
                    onChange={(e) =>
                      handleChange("backgroundColor", e.target.value)
                    }
                    className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                  />
                </div>
              </label>
            </div>
            {/* Row 1 - Col 2: Rotate */}
            <div className="flex flex-col gap-2">
              <p className="text-xs text-black font-semibold flex items-center gap-1">
                <RotateCcw size={14} className="text-gray-600" /> Rotation
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRotate}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                  type="button"
                  title="Rotate 45°"
                >
                  <RotateCcw size={16} />
                  <span className="text-xs">+45°</span>
                </button>
                <span className="text-xs text-gray-600">
                  {(selectedComponent?.rotation ?? 0) + "°"}
                </span>
              </div>
            </div>

            {/* Row 2 - Col 1: Opacity */}
            <div className="flex flex-col gap-2">
              <p className="text-xs text-black font-semibold flex items-center gap-1">
                <Layers size={14} className="text-gray-600" /> Opacity
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={Math.round((selectedComponent?.opacity ?? 1) * 100)}
                  onChange={(e) =>
                    handleChange("opacity", parseInt(e.target.value, 10) / 100)
                  }
                  className="w-full"
                  style={{ accentColor: "#6b7280" }}
                />
                <span className="text-xs text-gray-600 w-10 text-right">
                  {Math.round((selectedComponent?.opacity ?? 1) * 100)}%
                </span>
              </div>
            </div>

            {/* Row 2 - Col 2: Border radius (rectangle) or spacer */}
            {selectedComponent?.shapeType === "rectangle" ? (
              <div className="flex flex-col gap-2">
                <p className="text-xs text-black font-semibold flex items-center gap-1">
                  <Maximize size={14} className="text-gray-600" /> Border Radius
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="1"
                    value={selectedComponent?.borderRadius ?? 0}
                    onChange={(e) =>
                      handleChange("borderRadius", parseInt(e.target.value, 10))
                    }
                    className="w-full"
                    style={{ accentColor: "#6b7280" }}
                  />
                  <span className="text-xs text-gray-600 w-10 text-right">
                    {selectedComponent?.borderRadius ?? 0}px
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2" />
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ShapeToolbar;
